import {CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule, SecurityContext} from '@angular/core';
import {DecimalPipe, LocationStrategy, registerLocaleData} from '@angular/common';
import localeHe from '@angular/common/locales/he';
import localeHeExtra from '@angular/common/locales/extra/he';
import {AppService, ParameterHashLocationStrategy} from './app.service';
import {BrowserModule} from '@angular/platform-browser';

import {AppRoutingModule} from './app-routing.module';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BlockUIModule} from 'src/ngx-blockui/block-ui.module';
import {MarkdownModule, MARKED_OPTIONS} from 'ngx-markdown';

import { CoreModule } from './_core/core.module';
import { MatTelInput } from 'src/ngx-mat-intl-tel-input/ngx-mat-intl-tel-input.component';
import { HttpInterceptorProviders } from './_core/refresh-token-interceptor';

import { AppComponent } from './app.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { MessageSnackbarComponent } from './dialogs/message-snackbar/message-snackbar.component';
import { BottomSheetDialogComponent } from './dialogs/bottom-sheet-dialog/bottom-sheet-dialog.component';
import { MessageDialogComponent } from './dialogs/message-dialog/message-dialog.component';
import {TabitPayComponent} from './tabit-pay/tabit-pay.component';
import {TabitpayCheckoutPartialComponent} from './tabit-pay/tabitpay-checkout-partial/tabitpay-checkout-partial.component';
import {TabitpayCheckoutPayComponent} from './tabit-pay/tabitpay-checkout-pay/tabitpay-checkout-pay.component';
import {TabitpayEnterOrderComponent} from './tabit-pay/tabitpay-enter-order/tabitpay-enter-order.component';
import {TabitpayTicketComponent} from './tabit-pay/tabitpay-ticket/tabitpay-ticket.component';
import {TabitpayTicketSeatedComponent} from './tabit-pay/tabitpay-ticket-seated/tabitpay-ticket-seated.component';
import {LottieAnimationComponent} from './components/lottie-animation/lottie-animation.component';
import {AnimationDialogComponent} from './dialogs/animation-dialog/animation-dialog.component';
import {BannerCarouselComponent} from './components/banner-carousel/banner-carousel.component';
import {KeypadDialogComponent} from './dialogs/keypad-dialog/keypad-dialog.component';
import {VirtualRouteComponent} from './components/virtual-route/virtual-route.component';
import {PassorderDialogComponent} from 'src/app/tabit-pay/_dialogs/passorder-dialog/passorder-dialog.component';
import {InfoPageComponent} from './main/info-page/info-page.component';
import {OrderViewDialogComponent} from 'src/app/tabit-pay/_dialogs/order-view-dialog/order-view-dialog.component';
import {SmsDialogComponent} from './dialogs/sms-dialog/sms-dialog.component';
import {SignupPromptDialogComponent} from 'src/app/tabit-pay/_dialogs/signup-prompt-dialog/signup-prompt-dialog.component';
import {LoginDialogComponent} from './login/login-dialog/login-dialog.component';
import {TabitappBannerComponent} from './components/tabitapp-banner/tabitapp-banner.component';
import {BraintreeDialogComponent} from './dialogs/braintree-dialog/braintree-dialog.component';
import {TabitpayTicketChecksComponent} from './tabit-pay/tabitpay-ticket/tabitpay-ticket-checks/tabitpay-ticket-checks.component';
import {TabitpayCheckDialogComponent} from './tabit-pay/tabitpay-ticket/tabitpay-check-dialog/tabitpay-check-dialog.component';
import {ExternalPayDialogComponent} from './dialogs/external-pay-dialog/external-pay-dialog.component';
import {CardpointPayDialogComponent} from './dialogs/cardpoint-pay-dialog/cardpoint-pay-dialog.component';
import {HeartlandPayDialogComponent} from './dialogs/heartland-pay-dialog/heartland-pay-dialog.component';
import {StripePayDialogComponent} from './dialogs/stripe-pay-dialog/stripe-pay-dialog.component';
import {OfferSelectDialogComponent} from 'src/app/tabit-pay/_dialogs/offer-select-dialog/offer-select-dialog.component';
import {AuthorizenetPayDialogComponent} from './dialogs/authorizenet-pay-dialog/authorizenet-pay-dialog.component';
import {SharedOrderCreateComponent} from 'src/app/shared-order/shared-order-create/shared-order-create.component';
import {SharedOrderJoinComponent} from 'src/app/shared-order/shared-order-join/shared-order-join.component';
import {PowerByTabitComponent} from './components/power-by-tabit/power-by-tabit.component';
import {SharedOrderManageComponent} from 'src/app/shared-order/shared-order-manage/shared-order-manage.component';
import {AdyenDialogComponent} from './dialogs/adyen-dialog/adyen-dialog.component';
import {TipDialogComponent} from 'src/app/tabit-pay/_dialogs/tip-dialog/tip-dialog.component';
import {PaybyDinersDialogComponent} from 'src/app/tabit-pay/_dialogs/payby-diners-dialog/payby-diners-dialog.component';
import {PaySuccessDialogComponent} from 'src/app/tabit-pay/_dialogs/pay-success-dialog/pay-success-dialog.component';
import {SignupBannerComponent} from 'src/app/tabit-pay/_components/signup-banner/signup-banner.component';
import {NavepxDialogComponent} from './dialogs/navepx-dialog/navepx-dialog.component';
import { ImageDialogComponent } from './dialogs/image-dialog/image-dialog.component';
import { OrderItemsComponent } from './order-items/order-items.component';
import { OrderItemsSummaryComponent } from './order-items/order-items-summary/order-items-summary.component';
import { MenuComponent } from './order-items/menu/menu.component';
import { OfferComponent } from './order-items/offer/offer.component';
import { OfferItemDialogComponent } from './order-items/offer/offer-item-dialog/offer-item-dialog.component';
import { ItemGroupComponent } from './order-items/offer/item-group/item-group.component';
import { ModifierGroupComponent } from './order-items/offer/modifier-group/modifier-group.component';
import { GroupSaleComponent } from './order-items/group-sale/group-sale.component';
import { TabitOrderComponent } from './tabit-order/tabit-order.component';
import { TabitOrderSummaryComponent } from './tabit-order/tabit-order-summary/tabit-order-summary.component';
import { TabitOrderPayComponent } from './tabit-order/tabit-order-pay/tabit-order-pay.component';
import { TabitOrderExtrasComponent } from './tabit-order/tabit-order-extras/tabit-order-extras.component';
import { TabitOrderTipComponent } from './tabit-order/tabit-order-tip/tabit-order-tip.component';
import { TabitOrderInfoComponent } from './tabit-order/_components/tabit-order-info/tabit-order-info.component';
import { OfferSearchDialogComponent } from './order-items/offer-search-dialog/offer-search-dialog.component';
import { TabitpayDinerDialogComponent } from './tabit-pay/tabitpay-ticket/tabitpay-diner-dialog/tabitpay-diner-dialog.component';
import { TabitOrderEndComponent } from './tabit-order/tabit-order-end/tabit-order-end.component';
import { GiftcardDialogComponent } from './dialogs/giftcard-dialog/giftcard-dialog.component';
import { OrderLoyaltyComponent } from './tabit-order/order-loyalty/order-loyalty.component';
import { InlineKeypadComponent } from './components/inline-keypad/inline-keypad.component';
import { LoyaltyRegistrationFormComponent } from './tabit-order/order-loyalty/loyalty-registration-form/loyalty-registration-form.component';
import { OfferAttributesComponent } from './components/offer-attributes/offer-attributes.component';
import { OfferMarketingComponent } from './components/offer-marketing/offer-marketing.component';
import { TabOptionsBottomsheetComponent } from './dialogs/tab-options-bottomsheet/tab-options-bottomsheet.component';
import { AccountDialogComponent } from './login/account-dialog/account-dialog.component';
import { HistoryDialogComponent } from './login/history-dialog/history-dialog.component';
import { WalletDialogComponent } from './login/wallet-dialog/wallet-dialog.component';
import { TabitpayTabComponent } from './tabit-pay/tabitpay-tab/tabitpay-tab.component';
import { TabitpayTicketOffersComponent } from './tabit-pay/tabitpay-ticket/tabitpay-ticket-offers/tabitpay-ticket-offers.component';
import { InlineDinersComponent } from './components/inline-diners/inline-diners.component';
import { KashcashDialogComponent } from './dialogs/kashcash-dialog/kashcash-dialog.component';
import { FullFrameDialogComponent } from './dialogs/full-frame-dialog/full-frame-dialog.component';
import { MagensaPayDialogComponent } from './dialogs/magensa-pay-dialog/magensa-pay-dialog.component';
import { QRCodeComponent } from 'angularx-qrcode';
import { ItemInfoDialogComponent } from './dialogs/item-info-dialog/item-info-dialog.component';
import { TableNumberDialogComponent } from './dialogs/table-number-dialog/table-number-dialog.component';
import { OfferRequestDialogComponent } from './tabit-pay/_dialogs/offer-request-dialog/offer-request-dialog.component';
import { InlineTranslationPickerComponent } from './components/inline-translation-picker/inline-translation-picker.component';

registerLocaleData(localeHe, 'he', localeHeExtra);

@NgModule({
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	declarations: [
		AppComponent,
		SidenavComponent,
		MessageSnackbarComponent,
		BottomSheetDialogComponent,
		MessageDialogComponent,
		AnimationDialogComponent,
		KeypadDialogComponent,
		PassorderDialogComponent,
		OrderViewDialogComponent,
		SmsDialogComponent,
		SignupPromptDialogComponent,
		LoginDialogComponent,
		BraintreeDialogComponent,
		TabitpayCheckDialogComponent,
		ExternalPayDialogComponent,
		NavepxDialogComponent,
		VirtualRouteComponent,
		TabitappBannerComponent,
		TabitpayTicketComponent,
		TabitpayTicketSeatedComponent,
		TabitpayTicketChecksComponent,
		InfoPageComponent,
		TabitPayComponent,
		TabitpayCheckoutPartialComponent,
		TabitpayCheckoutPayComponent,
		TabitpayEnterOrderComponent,
		CardpointPayDialogComponent,
		HeartlandPayDialogComponent,
		StripePayDialogComponent,
		OfferSelectDialogComponent,
		AuthorizenetPayDialogComponent,
		SharedOrderCreateComponent,
		SharedOrderJoinComponent,
		PowerByTabitComponent,
		SharedOrderManageComponent,
		AdyenDialogComponent,
		TipDialogComponent,
		PaybyDinersDialogComponent,
		PaySuccessDialogComponent,
		SignupBannerComponent,
		ImageDialogComponent,
		OrderItemsComponent, OrderItemsSummaryComponent, MenuComponent, OfferComponent, OfferItemDialogComponent, ItemGroupComponent, ModifierGroupComponent, GroupSaleComponent, TabitOrderComponent, TabitOrderSummaryComponent, TabitOrderPayComponent, TabitOrderExtrasComponent, TabitOrderTipComponent, TabitOrderInfoComponent, OfferSearchDialogComponent, TabitpayDinerDialogComponent, TabitOrderEndComponent, GiftcardDialogComponent, OrderLoyaltyComponent, InlineKeypadComponent, LoyaltyRegistrationFormComponent, TabOptionsBottomsheetComponent, AccountDialogComponent, HistoryDialogComponent, WalletDialogComponent, TabitpayTabComponent, TabitpayTicketOffersComponent, InlineDinersComponent, KashcashDialogComponent, FullFrameDialogComponent, MagensaPayDialogComponent,
		ItemInfoDialogComponent,
		TableNumberDialogComponent,
		OfferRequestDialogComponent,
	],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		MatTelInput,
		CoreModule,
		BlockUIModule.forRoot(),
		QRCodeComponent,
		OfferMarketingComponent,
		OfferAttributesComponent,
		LottieAnimationComponent,
		BannerCarouselComponent,
		InlineTranslationPickerComponent,
		BlockUIModule.forRoot(),
		MarkdownModule.forRoot({
			sanitize: SecurityContext.NONE,
			markedOptions: {
				provide: MARKED_OPTIONS,
				useFactory: (appService: AppService) => appService.markedOptionsFactory(),
				deps: [AppService],
			},
		}),
	],
	providers: [
		{ provide: LOCALE_ID, useValue: 'he' },
		{
			provide: LocationStrategy,
			useClass: ParameterHashLocationStrategy
		},
		AppService,
		DecimalPipe,
		HttpInterceptorProviders,
	],
	exports: [
		OfferAttributesComponent,
		OfferMarketingComponent,
		LottieAnimationComponent,
		BannerCarouselComponent,
		InlineTranslationPickerComponent,
	],
	bootstrap: [AppComponent]
})
export class AppModule { }
